import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Button,
  Card,
  Badge,
  Alert,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';

import PrivateLayout from '../layouts/PrivateLayout';
import Panel from '../common/Panel';
import { useFetchParsedContent } from '../../services/claims';

const ParsedContentViewer = () => {
  const { claimId, fileId } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, error } = useFetchParsedContent(claimId, fileId);

  const handleCopy = async (content) => {
    try {
      await navigator.clipboard.writeText(content);
      toast.success('Content copied to clipboard!');
    } catch (err) {
      toast.error('Failed to copy content');
    }
  };

  const formatContent = (content) => {
    try {
      const parsed = JSON.parse(content);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return content;
    }
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading parsed content...</p>
        </div>
      );
    }

    if (error) {
      return (
        <Alert variant="danger">
          <Alert.Heading>Error Loading Content</Alert.Heading>
          <p>{error.message || 'Failed to load parsed content'}</p>
        </Alert>
      );
    }

    if (!data?.files || Object.keys(data.files).length === 0) {
      return (
        <Alert variant="info">
          <Alert.Heading>No Content Available</Alert.Heading>
          <p>No parsed content available for this file.</p>
        </Alert>
      );
    }

    const fileData = data.files[fileId];
    if (!fileData) {
      return (
        <Alert variant="warning">
          <Alert.Heading>File Not Found</Alert.Heading>
          <p>Could not find content for the specified file.</p>
        </Alert>
      );
    }

    const chunks = fileData.chunks || [];
    if (chunks.length === 0) {
      return (
        <Alert variant="info">
          <Alert.Heading>No Chunks Available</Alert.Heading>
          <p>File has been uploaded but not yet processed.</p>
        </Alert>
      );
    }

    const totalTokens = chunks.reduce(
      (sum, chunk) => sum + (chunk.tokenCount || 0),
      0,
    );

    return (
      <>
        <div
          className="bg-light border-bottom sticky-top"
          style={{ top: 0, zIndex: 100 }}
        >
          <Container className="py-3">
            <Row className="align-items-center">
              <Col>
                <h5 className="mb-0">
                  <strong>File:</strong> {fileData.fileName || 'Unknown'}
                </h5>
                {fileData.fileMeta?.processingTimestamp && (
                  <small className="text-muted">
                    Processed:{' '}
                    {new Date(
                      fileData.fileMeta.processingTimestamp,
                    ).toLocaleString()}
                  </small>
                )}
              </Col>
              <Col xs="auto">
                <Badge bg="primary" className="me-2">
                  {chunks.length} chunk{chunks.length !== 1 ? 's' : ''}
                </Badge>
                <Badge bg="info">{totalTokens.toLocaleString()} tokens</Badge>
              </Col>
            </Row>
          </Container>
        </div>

        <Container className="py-4">
          {chunks.map((chunk, index) => (
            <Card
              key={index}
              className="mb-3"
              style={{
                backgroundColor: index % 2 === 0 ? '#f8f9fa' : '#ffffff',
              }}
            >
              <Card.Header className="d-flex justify-content-between align-items-center">
                <div>
                  <Badge bg="secondary" className="me-2">
                    Chunk {chunk.chunkNumber || index + 1}
                  </Badge>
                  {chunk.pageNumber && (
                    <Badge bg="secondary" className="me-2">
                      Page {chunk.pageNumber}
                    </Badge>
                  )}
                  {chunk.tokenCount && (
                    <Badge bg="info">{chunk.tokenCount} tokens</Badge>
                  )}
                  {chunk.tokenCountModel && (
                    <small className="text-muted ms-2">
                      ({chunk.tokenCountModel})
                    </small>
                  )}
                </div>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => handleCopy(formatContent(chunk.parsedContent))}
                >
                  Copy
                </Button>
              </Card.Header>
              <Card.Body>
                <pre
                  style={{
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word',
                    fontFamily: "'Courier New', monospace",
                    fontSize: '0.9rem',
                    marginBottom: 0,
                  }}
                >
                  {formatContent(chunk.parsedContent)}
                </pre>
              </Card.Body>
            </Card>
          ))}
        </Container>
      </>
    );
  };

  return (
    <PrivateLayout>
      <Panel>
        <Container className="py-3">
          <Button
            variant="link"
            onClick={() => navigate(`/admin/profile/${claimId}`)}
            className="mb-3 ps-0"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Back to Claim
          </Button>
          <h3 className="mb-4">Parsed Document Content</h3>
        </Container>
        {renderContent()}
      </Panel>
    </PrivateLayout>
  );
};

export default ParsedContentViewer;
